import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ButtonGroup, Dropdown, Modal } from "react-bootstrap";
import { useIntl } from "react-intl";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import { PaginationBar } from "@components/shared/PaginationBar/PaginationBar";
import { ControlBar } from "@components/shared/ControlBar/ControlBar";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import UIService from "@services/UIService";
import SerialService from "@services/SerialService";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "@services/RestService";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import ISerial from "@models/ISerial";
import IOwTableCols from "@models/IOwTableCols";
import SortOrderEnum from "@models/SortOrderEnum";
import { useAppContextNonNull } from "@contexts/AppContext";
import { SerialsCsvUploadModal } from "./SerialsCsvUploadModal";
import { CreateSerialSidebar } from "./CreateSerialSidebar/CreateSerialSidebar";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import { ConfirmDeleteOverlayFn } from "@components/shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import IProcessSerialDTO from "@models/DTO/IProcessSerialDTO";
import { Link } from "react-router-dom";
import IGetSerialsQuery from "@models/DTO/IGetSerialsQuery";
import ICatalog from "@models/ICatalog";
import ICatalog_Item from "@models/ICatalog_Item";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import CatalogService from "@services/CatalogService";
import CatalogItemService from "@services/CatalogItemService";
import FastBoundSerialStatusEnum from "@models/FastBoundSerialStatusEnum";
import FastBoundService from "@services/FastBoundService";
import IFastbound_Account from "@models/IFastbound_Account";
import IVendor_Source from "@models/IVendor_Source";
import VendorSourceService from "@services/VendorSourceService";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import IOnReclassifySerialDTO from "@models/DTO/IOnReclassifySerialDTO";
import { ReclassifySerialModal } from "./ReclassifySerialModal/ReclassifySerialModal";

type Props = {};

export const Serials = (_props: Props) => {
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();

    // Data
    const [serials, setSerials] = useState<ISerial[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    // Fetch status
    const [isDataFetching, setIsDataFetching] = useState<boolean>(true);
    const [isInitialData, setIsInitialData] = useState<boolean>(true);

    // Search
    const [serialValue, setSerialValue] = useState<string>("");
    const [orderName, setOrderName] = useState<string>("");
    const [unassigned, setUnassigned] = useState<boolean>(false);
    const [fastboundSyncedFilter, setFastboundSyncedFilter] = useState<string>("");
    const [fastboundStatusFilter, setFastboundStatusFilter] = useState<string>("");
    const [fastboundAccounts, setFastboundAccounts] = useState<IFastbound_Account[]>([]);
    const [selectedFastboundAccountId, setSelectedFastboundAccountId] = useState<string>("");

    // Catalog / Catalog Item filters
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [selectedCatalog, setSelectedCatalog] = useState<ICatalog | null>(null);
    const [typeaheadItems, setTypeaheadItems] = useState<ICatalog_Item[]>([]);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<ICatalog_Item | null>(null);
    const [resetTypeahead, setResetTypeahead] = useState<boolean>(false);

    // Vendor Source filter
    const [vendorSourceOptions, setVendorSourceOptions] = useState<IVendor_Source[]>([]);
    const [selectedVendorSource, setSelectedVendorSource] = useState<IVendor_Source | null>(null);
    const [resetVendorSource, setResetVendorSource] = useState<boolean>(false);

    // Sort
    const [sortCol, setSortCol] = useState<string>("created_at");
    const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.DESC);

    // Pagination
    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(25);
    const [hasPaginationChange, setHasPaginationChange] = useState<boolean>(false);
    const [hasReset, setHasReset] = useState<boolean>(false);

    // Upload modal
    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

    // Delete
    const [sourceDeleteElem, setSourceDeleteElem] = useState<string | null>(null);
    const [rowToDelete, setRowToDelete] = useState<ISerial | null>(null);

    // Bulk delete (OR-1550): selection persists across pages; only unassigned serials can be selected.
    const [selectedSerials, setSelectedSerials] = useState<Map<number, ISerial>>(new Map());
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState<boolean>(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState<boolean>(false);
    // Set synchronously so a second click on Delete is dropped before the first request returns.
    const isBulkDeletingRef = useRef<boolean>(false);
    const [bulkDeleteOutcome, setBulkDeleteOutcome] = useState<{ deleted: number; failures: { value: string; reason: string }[] } | null>(
        null
    );

    // Set finished product (re-SKU, OR-1459): a BOM v2 surface, gated like the BOM editor.
    const canReclassify = !!supplierModel?.has_bom_v2;
    const [serialToReclassify, setSerialToReclassify] = useState<ISerial | null>(null);

    const fetchSerials = useCallback(async () => {
        try {
            setIsDataFetching(true);

            const query: IGetSerialsQuery = {
                limit: limit.toString(),
                page: offset.toString(),
                order_by: sortCol,
                order_dir: sortOrder,
            };

            if (serialValue) {
                query.serial = serialValue;
            }

            if (orderName) {
                query.order_name = orderName;
            }

            if (unassigned) {
                query.unassigned = "true";
            }

            if (selectedCatalog) {
                query.catalog_id = selectedCatalog.id.toString();
            }

            if (selectedCatalogItem) {
                query.catalog_item_id = selectedCatalogItem.id.toString();
            }

            if (fastboundSyncedFilter) {
                query.has_fastbound = fastboundSyncedFilter;
            }

            if (fastboundStatusFilter) {
                query.fastbound_status = fastboundStatusFilter;
            }

            if (selectedFastboundAccountId) {
                query.fastbound_account_id = selectedFastboundAccountId;
            }

            if (selectedVendorSource) {
                query.vendor_source_id = selectedVendorSource.id.toString();
            }

            const data = await SerialService.getSerials(query, userModel, supplierModel, vendorModel);
            setSerials(data.serials || []);
            setTotalCount(data.totalCount || 0);
        } catch (error) {
            console.error("Error fetching serials:", error);
            setSerials([]);
            setTotalCount(0);
        } finally {
            setIsDataFetching(false);
            setIsInitialData(false);
            setHasPaginationChange(false);
        }
    }, [
        userModel,
        supplierModel,
        vendorModel,
        offset,
        limit,
        sortCol,
        sortOrder,
        serialValue,
        orderName,
        unassigned,
        selectedCatalog,
        selectedCatalogItem,
        fastboundSyncedFilter,
        fastboundStatusFilter,
        selectedFastboundAccountId,
        selectedVendorSource,
    ]);

    const fetchCatalogs = useCallback(async () => {
        try {
            const data = await CatalogService.getCatalogs(userModel, supplierModel, vendorModel);
            setCatalogs(data.catalogs || []);
        } catch (error) {
            console.error("Error fetching catalogs:", error);
        }
    }, [userModel, supplierModel, vendorModel]);

    const fetchFastboundAccounts = useCallback(async () => {
        if (!supplierModel?.Supplier_Integration?.has_fastbound) return;
        try {
            const accounts = await FastBoundService.getAccounts(userModel, supplierModel);
            setFastboundAccounts(accounts || []);
        } catch (error) {
            console.error("Error fetching FastBound accounts:", error);
        }
    }, [userModel, supplierModel]);

    useEffect(() => {
        window.document.title = "Orderwerks >> Serials";
        fetchSerials();
        fetchCatalogs();
        fetchFastboundAccounts();
    }, []);

    useEffect(() => {
        if (hasPaginationChange) {
            fetchSerials();
        }
    }, [hasPaginationChange]);

    useEffect(() => {
        if (hasReset) {
            fetchSerials();
            setHasReset(false);
        }
    }, [hasReset]);

    useEffect(() => {
        fetchSerials();
    }, [sortCol, sortOrder]);

    const handleChange = (e: any) => {
        const value = e.target.value;

        switch (e.target.name) {
            case "serialValue":
                setSerialValue(value);
                break;
            case "orderName":
                setOrderName(value);
                break;
        }
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter") {
            performSearch();
        }
    };

    const performSearch = () => {
        setOffset(0);
        fetchSerials();
    };

    const handleCatalogSelect = (catalog: ICatalog | null) => {
        setSelectedCatalog(catalog);
        setSelectedCatalogItem(null);
        setTypeaheadItems([]);
        setResetTypeahead(true);
    };

    const doTypeaheadCatalogItem = async (searchPhrase: string) => {
        if (!searchPhrase || !selectedCatalog) return;

        try {
            const data = await CatalogItemService.typeaheadCatalogItems(
                selectedCatalog.guid,
                searchPhrase,
                userModel,
                supplierModel,
                vendorModel,
                { onlyWithSerials: true }
            );
            setTypeaheadItems(data.catalog_items || []);
        } catch (error) {
            console.error("Error searching catalog items:", error);
        }
    };

    const selectCatalogItem = (item: ICatalog_Item) => {
        setSelectedCatalogItem(item);
    };

    const lookupVendorSource = async (name: string) => {
        if (!name) return;
        try {
            const vendorSources = await VendorSourceService.getVendorSources(name, userModel, supplierModel);
            setVendorSourceOptions(vendorSources);
        } catch (error) {
            console.error("Error searching vendor sources:", error);
        }
    };

    const selectVendorSource = (option: IVendor_Source) => {
        setSelectedVendorSource(option);
    };

    const resetVendorSourceFn = () => {
        setSelectedVendorSource(null);
        setResetVendorSource(false);
    };

    const resetFilters = () => {
        setSerialValue("");
        setOrderName("");
        setUnassigned(false);
        setFastboundSyncedFilter("");
        setFastboundStatusFilter("");
        setSelectedFastboundAccountId("");
        setSelectedCatalog(null);
        setSelectedCatalogItem(null);
        setTypeaheadItems([]);
        setResetTypeahead(true);
        setSelectedVendorSource(null);
        setVendorSourceOptions([]);
        setResetVendorSource(true);
        setOffset(0);
        setLimit(25);
        setTotalCount(0);
        setSerials([]);
        setHasReset(true);
    };

    const toggleSortDir = (_sortCol: string, _sortOrder: SortOrderEnum) => {
        setSortCol(_sortCol);
        setSortOrder(_sortOrder);
    };

    const paginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
        setHasPaginationChange(true);
    };

    const downloadCsv = async () => {
        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.SerialsCsvTemplate,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
            });

            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to download CSV: ${response.statusText}`);
            }

            const csvData = await response.text();
            UIService.downloadCSVData(csvData, "serials-template.csv");
        } catch (error) {
            console.error("Error downloading CSV template:", error);
        }
    };

    const cancelCreateSidebar = (refresh?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refresh) {
            fetchSerials();
        }
    };

    const openCreateSidebar = () => {
        uiContext.setSidebarComponent(<CreateSerialSidebar cancel={cancelCreateSidebar} />);
        uiContext.toggleSidebar(true);
    };

    const confirmDelete = (row: ISerial, index: number) => {
        setSourceDeleteElem(`row-${index}`);
        setRowToDelete(row);
    };

    const commitDelete = async () => {
        if (!rowToDelete) return;

        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.SerialsProcessSerial,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel,
            });

            const body: Partial<IProcessSerialDTO> = {
                serial: rowToDelete.value,
                serialId: rowToDelete.id,
                deleteFlag: true,
            };

            const response = await fetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || `Failed to delete serial: ${response.statusText}`);
            }

            const deletedId = rowToDelete.id;
            setSelectedSerials((prev) => {
                if (!prev.has(deletedId)) return prev;
                const next = new Map(prev);
                next.delete(deletedId);
                return next;
            });

            fetchSerials();
        } catch (error) {
            console.error("Error deleting serial:", error);
        } finally {
            setSourceDeleteElem(null);
            setRowToDelete(null);
        }
    };

    /** The API deletes only serials that are not assigned to an order (and not released). */
    const isDeletable = (row: ISerial) => !row.order_id && !row.order_item_id;

    const toggleSerialSelection = (row: ISerial, checked: boolean) => {
        setSelectedSerials((prev) => {
            const next = new Map(prev);
            if (checked) next.set(row.id, row);
            else next.delete(row.id);
            return next;
        });
    };

    const selectAllOnPage = () => {
        setSelectedSerials((prev) => {
            const next = new Map(prev);
            serials.filter(isDeletable).forEach((row) => next.set(row.id, row));
            return next;
        });
    };

    const clearSelection = () => setSelectedSerials(new Map());

    const openBulkDelete = () => {
        setBulkDeleteOutcome(null);
        setShowBulkDeleteModal(true);
    };

    const closeBulkDelete = () => {
        if (isBulkDeletingRef.current) return;
        setShowBulkDeleteModal(false);
        setBulkDeleteOutcome(null);
    };

    const commitBulkDelete = async () => {
        if (isBulkDeletingRef.current) return;

        const selected = Array.from(selectedSerials.values());
        if (selected.length === 0) return;

        isBulkDeletingRef.current = true;
        setIsBulkDeleting(true);

        try {
            const result = await SerialService.bulkDeleteSerials(
                selected.map((row) => row.id),
                userModel,
                supplierModel,
                vendorModel
            );

            // Serials that could not be deleted stay selected; the rest are gone.
            const valueById = new Map(selected.map((row) => [row.id, row.value]));
            const failedIds = new Set(result.errors.map((error) => error.serialId));
            setSelectedSerials(new Map(selected.filter((row) => failedIds.has(row.id)).map((row) => [row.id, row])));

            if (result.deleted > 0) {
                uiContext.notifyFn(
                    null,
                    {
                        title: intl.formatMessage({ id: "delete_serials", defaultMessage: "Delete Serials" }),
                        message: intl.formatMessage(
                            { id: "serials_deleted_count", defaultMessage: "Deleted {count, plural, one {# serial} other {# serials}}" },
                            { count: result.deleted }
                        ),
                        level: NotificationLevelEnum.success,
                        position: NotificationPositionEnum.bc,
                    },
                    true
                );
            }

            if (result.errors.length > 0) {
                setBulkDeleteOutcome({
                    deleted: result.deleted,
                    failures: result.errors.map((error) => ({
                        value: valueById.get(error.serialId) ?? `#${error.serialId}`,
                        reason: error.reason,
                    })),
                });
            } else {
                setShowBulkDeleteModal(false);
            }

            fetchSerials();
        } catch (error: any) {
            console.error("Error bulk deleting serials:", error);
            uiContext.notifyFn(
                null,
                {
                    title: intl.formatMessage({ id: "delete_serials", defaultMessage: "Delete Serials" }),
                    message: error.message || "Error deleting serials",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                },
                true
            );
            fetchSerials();
        } finally {
            isBulkDeletingRef.current = false;
            setIsBulkDeleting(false);
        }
    };

    const cancelDelete = () => {
        setSourceDeleteElem(null);
        setRowToDelete(null);
    };

    const handleReclassified = (result: IOnReclassifySerialDTO) => {
        setSerialToReclassify(null);

        const notification: INotification = {
            title: intl.formatMessage({ id: "set_finished_product", defaultMessage: "Set finished product" }),
            message: intl.formatMessage(
                { id: "serial_classified_as_sku", defaultMessage: "Serial {value} is now {sku}" },
                { value: result.value, sku: result.sku }
            ),
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };
        uiContext.notifyFn(null, notification, true);

        fetchSerials();
    };

    const formatDate = (date: string | Date) => {
        if (!date) return "";
        return new Date(date).toLocaleDateString();
    };

    const columns: IOwTableCols[] = [
        {
            title: "",
            valueProp: "id",
            renderFn: (row: ISerial) =>
                isDeletable(row) ? (
                    <input
                        type="checkbox"
                        className="form-check-input pointer"
                        aria-label={intl.formatMessage(
                            { id: "select_serial_value", defaultMessage: "Select serial {value}" },
                            { value: row.value }
                        )}
                        checked={selectedSerials.has(row.id)}
                        disabled={isBulkDeleting}
                        onChange={(e) => toggleSerialSelection(row, e.target.checked)}
                    />
                ) : (
                    <input
                        type="checkbox"
                        className="form-check-input"
                        aria-label={intl.formatMessage({
                            id: "serial_assigned_cannot_delete",
                            defaultMessage: "Assigned to an order, cannot be deleted",
                        })}
                        title={intl.formatMessage({
                            id: "serial_assigned_cannot_delete",
                            defaultMessage: "Assigned to an order, cannot be deleted",
                        })}
                        disabled
                    />
                ),
            renderFnEmitsRow: true,
        },
        {
            title: "actions",
            valueProp: "id",
            renderFn: (row: ISerial, index: number) => {
                if (row.order_id) return null;
                return (
                    <div className="d-flex align-items-center gap-1">
                        <OwDeleteEditActions isEditMode={false} hideEdit={true} onTrashClickFn={() => confirmDelete(row, index)} />
                        {canReclassify && (
                            <ButtonGroup>
                                <Button
                                    variant={"primary"}
                                    size={"sm"}
                                    title={intl.formatMessage({ id: "set_finished_product", defaultMessage: "Set finished product" })}
                                    aria-label={intl.formatMessage({ id: "set_finished_product", defaultMessage: "Set finished product" })}
                                    onClick={() => setSerialToReclassify(row)}>
                                    <i className={"fas fa-exchange-alt"}></i>
                                </Button>
                            </ButtonGroup>
                        )}
                    </div>
                );
            },
            renderFnEmitsRow: true,
        },
        {
            title: "serial",
            valueProp: "value",
            isSortable: true,
            sortCol: "value",
            sortOrderFn: () => sortOrder,
            sortFn: (_sortCol: string, _sortOrder: SortOrderEnum) => {
                toggleSortDir("value", _sortOrder);
            },
        },
        {
            title: "catalog",
            valueProp: "Catalog",
            renderFn: (row: any) => {
                return row.Catalog?.name || "-";
            },
            renderFnEmitsRow: true,
        },
        {
            title: "sku",
            valueProp: "Catalog_Item",
            renderFn: (row: any) => {
                return row.Catalog_Item?.sku || "-";
            },
            renderFnEmitsRow: true,
        },
        {
            title: "name",
            valueProp: "Catalog_Item",
            renderFn: (row: any) => {
                return row.Catalog_Item?.name || "-";
            },
            renderFnEmitsRow: true,
        },
        {
            title: "order",
            valueProp: "Order",
            renderFn: (row: any) => {
                if (!row.Order) return "-";
                return <Link to={`/orders/${row.Order?.guid}/entry`}>{row.Order?.name}</Link>;
            },
            renderFnEmitsRow: true,
            isSortable: true,
            sortCol: "order_name",
            sortOrderFn: () => sortOrder,
            sortFn: (_sortCol: string, _sortOrder: SortOrderEnum) => {
                toggleSortDir("order_name", _sortOrder);
            },
        },
        {
            title: "vendor",
            valueProp: "Vendor",
            renderFn: (row: any) => {
                return row.Vendor?.name || "-";
            },
            renderFnEmitsRow: true,
            isVendorOverride: true,
        },
        {
            title: "vendor_source",
            valueProp: "Vendor_Source",
            renderFn: (row: any) => {
                return row.Vendor_Source?.name || "-";
            },
            renderFnEmitsRow: true,
        },
        {
            title: "created_at",
            valueProp: "created_at",
            renderFn: (row: any) => {
                return formatDate(row.created_at);
            },
            renderFnEmitsRow: true,
            isSortable: true,
            sortCol: "created_at",
            sortOrderFn: () => sortOrder,
            sortFn: (_sortCol: string, _sortOrder: SortOrderEnum) => {
                toggleSortDir("created_at", _sortOrder);
            },
        },
    ];

    if (supplierModel?.Supplier_Integration?.has_fastbound) {
        columns.push({
            title: "fastbound",
            valueProp: "fastbound_item_id",
            renderFn: (row: ISerial) => {
                if (!row.fastbound_item_id) {
                    return <i className="fas fa-minus-circle text-muted" title="Not synced with FastBound" />;
                }
                const statusMap: Record<string, { className: string; label: string }> = {
                    [FastBoundSerialStatusEnum.ACTIVE]: { className: "bg-success", label: "Active" },
                    [FastBoundSerialStatusEnum.DISPOSED]: { className: "bg-info", label: "Disposed" },
                    [FastBoundSerialStatusEnum.DELETED]: { className: "bg-danger", label: "Deleted" },
                    [FastBoundSerialStatusEnum.THEFT_LOSS]: { className: "bg-danger", label: "Theft/Loss" },
                    [FastBoundSerialStatusEnum.DESTROYED]: { className: "bg-danger", label: "Destroyed" },
                    [FastBoundSerialStatusEnum.DISMISSED]: { className: "bg-secondary", label: "Dismissed" },
                };
                const status = row.fastbound_status || FastBoundSerialStatusEnum.ACTIVE;
                const badge = statusMap[status] || { className: "bg-secondary", label: status };
                const isPending = !row.catalog_item_id;
                return (
                    <span className="d-flex align-items-center gap-1">
                        <i className="fas fa-check-circle text-success" title="Synced with FastBound" />
                        <span className={`badge ${badge.className}`}>{badge.label}</span>
                        {isPending && <span className="badge bg-warning text-dark">Pending</span>}
                    </span>
                );
            },
            renderFnEmitsRow: true,
        });
    }

    const pendingFastBoundSection = supplierModel?.Supplier_Integration?.has_fastbound ? (
        <Link to="/business/integrations/fastbound/pending_serials" className="btn btn-sm btn-outline-warning">
            <i className="fas fa-clock me-1"></i>
            Pending FastBound
        </Link>
    ) : null;

    const csvActionsSection = userModel?.isSupplierUser ? (
        <Dropdown>
            <Dropdown.Toggle size="sm" variant="primary" id="csv-actions-dropdown">
                <i className="fas fa-file-csv me-2"></i>
                CSV
            </Dropdown.Toggle>
            <Dropdown.Menu className={UIService.isMainViewLight() ? "" : "bg-secondary"}>
                <Dropdown.Item className="bg-primary text-white" onClick={downloadCsv}>
                    <i className="fas fa-download me-2"></i>
                    Download CSV Template
                </Dropdown.Item>
                <Dropdown.Item className="bg-danger text-white" onClick={() => setShowUploadModal(true)}>
                    <i className="fas fa-upload me-2"></i>
                    Upload CSV
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    ) : null;

    return (
        <div className={"Serials pb-5 pb-md-0"}>
            <div className={"row mt-2"}>
                <div className={"col-12 d-flex flex-row align-items-center justify-content-between"}>
                    <div className={"d-flex align-items-center"}>
                        <OwTitle title={"serials"} leftIconName={"fad fa-barcode"} className={"me-auto"} />
                        <div className={"ms-2"}>
                            <OwButton
                                key="create-serial"
                                title={"create"}
                                fn={openCreateSidebar}
                                leftIconClass={"fas fa-plus"}
                                className={"btn-warning btn-sm me-2"}
                                centerIconClass={"fas fa-plus"}
                            />
                        </div>
                    </div>

                    <div className={"d-flex ms-auto d-print-none align-items-end mb-2"}>
                        <ControlBar
                            sections={[
                                {
                                    name: "pending_fastbound",
                                    elements: [pendingFastBoundSection],
                                },
                                {
                                    name: "csv_actions",
                                    elements: [csvActionsSection],
                                },
                            ]}
                        />
                    </div>
                </div>
            </div>

            <div className={"mb-2"}>
                <SearchSection>
                    <div className={"row mb-2 align-items-end"}>
                        <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                            <OwLabel title={"serial"} />
                            <OwFnInput fn={handleChange} name={"serialValue"} value={serialValue} onKeyDownFn={enterSearch} />
                        </div>
                        <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                            <OwLabel title={"order"} />
                            <OwFnInput fn={handleChange} name={"orderName"} value={orderName} onKeyDownFn={enterSearch} />
                        </div>
                        <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                            <OwLabel title={"catalog"} />
                            <OwSelectOption
                                options={catalogs}
                                valueProp={"id"}
                                titleProp={"name"}
                                fn={handleCatalogSelect}
                                name={"catalog"}
                                showBlankFirst={true}
                                selectedOption={selectedCatalog}
                                selectedOptionProp={"id"}
                                emitObject={true}
                                sizeClass={"form-select-sm"}
                            />
                        </div>
                        <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                            <OwLabel title={"catalog_item"} />
                            <TypeaheadFn
                                lookupFn={doTypeaheadCatalogItem}
                                availableOptions={typeaheadItems}
                                selectedOption={selectedCatalogItem}
                                selectedOptionFn={selectCatalogItem}
                                titleProp={"sku"}
                                subTitleProp={"name"}
                                placeholder={"Search by SKU or Name"}
                                disabled={!selectedCatalog}
                                resetTypeahead={resetTypeahead}
                                resetTypeaheadCallbackFn={() => setResetTypeahead(false)}
                                clearSelectionFn={() => setSelectedCatalogItem(null)}
                                emitSelectionSetup={{ emitObject: true }}
                            />
                        </div>
                        <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2 d-flex align-items-center"}>
                            <div className="form-check">
                                <OwToggle
                                    name={"unassigned"}
                                    label={"unassigned"}
                                    fn={(e) => setUnassigned(e.target.value)}
                                    active={unassigned}
                                    labelOnRight={true}
                                />
                            </div>
                        </div>
                        {supplierModel?.has_purchase_orders && (
                            <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                                <OwLabel title={"vendor_source"} />
                                <TypeaheadFn
                                    lookupFn={lookupVendorSource}
                                    availableOptions={vendorSourceOptions}
                                    selectedOptionFn={selectVendorSource}
                                    selectedOption={selectedVendorSource}
                                    valueProp={"guid"}
                                    titleProp={"name"}
                                    resetTypeahead={resetVendorSource}
                                    resetTypeaheadCallbackFn={resetVendorSourceFn}
                                    clearSelectionFn={() => setSelectedVendorSource(null)}
                                    emitSelectionSetup={{ emitObject: true }}
                                />
                            </div>
                        )}
                        {supplierModel?.Supplier_Integration?.has_fastbound && (
                            <>
                                <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                                    <OwLabel title={"fastbound_synced"} />
                                    <select
                                        className={"form-select form-select-sm"}
                                        value={fastboundSyncedFilter}
                                        onChange={(e) => setFastboundSyncedFilter(e.target.value)}>
                                        <option value="">All</option>
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </select>
                                </div>
                                <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                                    <OwLabel title={"fastbound_status"} />
                                    <select
                                        className={"form-select form-select-sm"}
                                        value={fastboundStatusFilter}
                                        onChange={(e) => setFastboundStatusFilter(e.target.value)}>
                                        <option value="">All</option>
                                        <option value={FastBoundSerialStatusEnum.ACTIVE}>Active</option>
                                        <option value={FastBoundSerialStatusEnum.DISPOSED}>Disposed</option>
                                        <option value={FastBoundSerialStatusEnum.DELETED}>Deleted</option>
                                        <option value={FastBoundSerialStatusEnum.THEFT_LOSS}>Theft/Loss</option>
                                        <option value={FastBoundSerialStatusEnum.DESTROYED}>Destroyed</option>
                                        <option value={FastBoundSerialStatusEnum.DISMISSED}>Dismissed</option>
                                    </select>
                                </div>
                                <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                                    <OwLabel title={"fastbound_account"} />
                                    <select
                                        className={"form-select form-select-sm"}
                                        value={selectedFastboundAccountId}
                                        onChange={(e) => setSelectedFastboundAccountId(e.target.value)}>
                                        <option value="">All</option>
                                        {fastboundAccounts.map((account) => (
                                            <option key={account.id} value={account.id.toString()}>
                                                {account.name || `Account #${account.fastbound_account_number}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}
                        <div className={"col-auto"}>
                            <OwButton title={"search"} fn={performSearch} className={"btn-primary me-2"} />
                            <OwButton title={"reset"} fn={resetFilters} className={"btn-danger"} />
                        </div>
                    </div>
                </SearchSection>
            </div>

            {selectedSerials.size > 0 ? (
                <div
                    className="d-flex align-items-center flex-wrap gap-2 mb-2 p-2 rounded secondary-bg-color"
                    data-testid="serials-selection-bar">
                    <span className="me-1 fw-bold">
                        {intl.formatMessage({ id: "n_selected", defaultMessage: "{count} selected" }, { count: selectedSerials.size })}
                    </span>
                    <OwButton
                        title="delete_selected"
                        leftIconClass="fas fa-trash"
                        fn={openBulkDelete}
                        className="btn-danger btn-sm"
                        disabled={isBulkDeleting}
                    />
                    <OwButton title="select_all_on_page" fn={selectAllOnPage} className="btn-secondary btn-sm" disabled={isBulkDeleting} />
                    <OwButton
                        title="clear_selection"
                        fn={clearSelection}
                        className="btn-outline-secondary btn-sm"
                        disabled={isBulkDeleting}
                    />
                </div>
            ) : (
                serials.some(isDeletable) && (
                    <div className="mb-2">
                        <OwButton title="select_all_on_page" fn={selectAllOnPage} className="btn-secondary btn-sm" />
                    </div>
                )
            )}

            <div className={"table-responsive position-relative"}>
                <ConfirmDeleteOverlayFn
                    targetElem={sourceDeleteElem}
                    handleConfirm={commitDelete}
                    handleCancel={cancelDelete}
                    skipMainView={true}
                    nestedParentElem={"serials-table"}
                />
                <OwFnTable
                    id={"serials-table"}
                    columns={columns}
                    dataSet={serials}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                    currentSortCol={sortCol}
                    wrappingClassName={"w-100"}
                />
            </div>

            {serials && serials.length > 0 ? (
                <PaginationBar offset={offset} limit={limit} totalRecords={totalCount} onPaginationChanged={paginationChanged} />
            ) : null}

            <Modal show={showBulkDeleteModal} onHide={closeBulkDelete} centered backdrop={isBulkDeleting ? "static" : true}>
                <Modal.Header closeButton={!isBulkDeleting}>
                    <Modal.Title>{intl.formatMessage({ id: "delete_serials", defaultMessage: "Delete Serials" })}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {bulkDeleteOutcome ? (
                        <div className="alert alert-warning small mb-0" data-testid="bulk-delete-outcome">
                            <div className="fw-bold mb-1">
                                {intl.formatMessage(
                                    {
                                        id: "serials_deleted_with_failures",
                                        defaultMessage: "Deleted {deleted, plural, one {# serial} other {# serials}}. Not deleted:",
                                    },
                                    { deleted: bulkDeleteOutcome.deleted }
                                )}
                            </div>
                            <ul className="mb-0 ps-3" style={{ maxHeight: 240, overflowY: "auto" }}>
                                {bulkDeleteOutcome.failures.map((failure, index) => (
                                    <li key={`${failure.value}-${index}`}>
                                        <span className="font-monospace">{failure.value}</span>: {failure.reason}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <>
                            <p data-testid="bulk-delete-confirm-text">
                                {intl.formatMessage(
                                    {
                                        id: "delete_serials_confirm",
                                        defaultMessage:
                                            "Delete {count, plural, one {# serial} other {# serials}}? Inventory counts and component reservations are reversed for each one. This cannot be undone.",
                                    },
                                    { count: selectedSerials.size }
                                )}
                            </p>
                            <div className="small font-monospace text-muted" style={{ maxHeight: 160, overflowY: "auto" }}>
                                {Array.from(selectedSerials.values())
                                    .map((row) => row.value)
                                    .join(", ")}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {/* Distinct keys: Close must not reuse the Delete button's instance (its click lock and width). */}
                    {bulkDeleteOutcome ? (
                        <OwButton key="close" title="close" fn={closeBulkDelete} className="btn-secondary" />
                    ) : (
                        <>
                            <OwButton
                                key="delete"
                                title="delete"
                                fn={commitBulkDelete}
                                className="btn-danger"
                                disabled={isBulkDeleting || selectedSerials.size === 0}
                                active={isBulkDeleting}
                                disableOnActive={true}
                            />
                            <OwButton
                                key="cancel"
                                title="cancel"
                                fn={closeBulkDelete}
                                className="btn-secondary"
                                disabled={isBulkDeleting}
                            />
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            <ReclassifySerialModal
                serial={serialToReclassify}
                closeFn={() => setSerialToReclassify(null)}
                onReclassified={handleReclassified}
            />

            <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg" className="SerialsCsvUploadModal" centered>
                <Modal.Body>
                    <SerialsCsvUploadModal
                        closeModalFn={() => {
                            setShowUploadModal(false);
                            fetchSerials();
                        }}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};
